import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import type { FeedbackType, FeedbackPriority } from '@crm/shared'
import { createFeedback } from './feedback.api'

interface Props {
  onClose: () => void
}

export function FeedbackCreateModal({ onClose }: Props) {
  const [type, setType] = useState<FeedbackType>('BUG')
  const [priority, setPriority] = useState<FeedbackPriority>('NORMAL')
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [file, setFile] = useState<File | null>(null)
  const [error, setError] = useState<string | null>(null)
  const qc = useQueryClient()

  const mutation = useMutation({
    mutationFn: () =>
      createFeedback({
        type,
        priority,
        title: title.trim(),
        description: description.trim() || null,
        screenshot: file,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['feedback'] })
      onClose()
    },
    onError: (e: unknown) => {
      const msg =
        (e as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
          ?.message ?? 'Ошибка создания'
      setError(msg)
    },
  })

  function onFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0] ?? null
    if (f && f.size > 5 * 1024 * 1024) {
      setError('Файл больше 5 MB')
      return
    }
    if (f && !['image/jpeg', 'image/png', 'image/webp'].includes(f.type)) {
      setError('Разрешены только jpg, png, webp')
      return
    }
    setError(null)
    setFile(f)
  }

  function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!title.trim()) {
      setError('Введите заголовок')
      return
    }
    mutation.mutate()
  }

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-3"
      onClick={onClose}
    >
      <form
        onSubmit={onSubmit}
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-lg w-full max-w-lg max-h-[90vh] overflow-auto p-5 space-y-4"
      >
        <h2 className="text-lg font-semibold">Новая обратная связь</h2>

        <div className="grid grid-cols-2 gap-3">
          <label className="text-sm">
            <span className="text-gray-600">Тип</span>
            <select
              value={type}
              onChange={(e) => setType(e.target.value as FeedbackType)}
              className="mt-1 w-full border rounded px-2 py-1.5 text-sm"
            >
              <option value="BUG">🐛 Баг</option>
              <option value="IDEA">💡 Идея</option>
            </select>
          </label>
          <label className="text-sm">
            <span className="text-gray-600">Приоритет</span>
            <select
              value={priority}
              onChange={(e) => setPriority(e.target.value as FeedbackPriority)}
              className="mt-1 w-full border rounded px-2 py-1.5 text-sm"
            >
              <option value="CRITICAL">Критично</option>
              <option value="NORMAL">Обычный</option>
              <option value="LOW">Низкий</option>
            </select>
          </label>
        </div>

        <label className="block text-sm">
          <span className="text-gray-600">Заголовок</span>
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={200}
            className="mt-1 w-full border rounded px-2 py-1.5 text-sm"
            placeholder="Кратко о проблеме или идее"
          />
        </label>

        <label className="block text-sm">
          <span className="text-gray-600">Описание</span>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={5}
            maxLength={10000}
            className="mt-1 w-full border rounded px-2 py-1.5 text-sm"
            placeholder="Шаги воспроизведения, что ожидали, что получили"
          />
        </label>

        <label className="block text-sm">
          <span className="text-gray-600">Скриншот (jpg/png/webp, до 5 MB)</span>
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp"
            onChange={onFileChange}
            className="mt-1 w-full text-sm"
          />
          {file && (
            <div className="mt-1 text-xs text-gray-500">
              {file.name} ({(file.size / 1024).toFixed(0)} КБ)
            </div>
          )}
        </label>

        {error && <div className="text-sm text-red-600">{error}</div>}

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100 rounded"
          >
            Отмена
          </button>
          <button
            type="submit"
            disabled={mutation.isPending}
            className="px-4 py-1.5 text-sm bg-brand-600 text-white rounded hover:bg-brand-700 disabled:opacity-50"
          >
            {mutation.isPending ? 'Отправка…' : 'Отправить'}
          </button>
        </div>
      </form>
    </div>
  )
}
